// Export da Dobra de Nicho: TXT (Blob) + PDF (jspdf), a partir dos dados JÁ em
// memória (analysis.result + reports). Um único "modelo" (lista de nós) alimenta
// os dois renderizadores. A seção de Insights de canal é OPCIONAL (análises
// antigas/cache não têm result.insights -> a seção é omitida).
import { jsPDF } from 'jspdf';
import { toast } from 'sonner';

const LOGO_URL = '/logo-darkplanner.png';
const SITE_URL = 'darkplanner.com.br';

// Carrega uma imagem (logo) como dataURL pra embutir no PDF (jsPDF.addImage).
// Best-effort: se falhar (offline/404), retorna null e o PDF sai só com texto.
async function loadDataUrl(url) {
  try {
    const resp = await fetch(url);
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return await new Promise((res) => {
      const fr = new FileReader();
      fr.onload = () => res(fr.result);
      fr.onerror = () => res(null);
      fr.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// Sanitiza p/ o jsPDF (fontes padrão = WinAnsi/Latin-1): a pontuação tipográfica
// (em/en dash, aspas curvas, reticências, setas) tem métrica de largura ruim e
// quebra o cálculo de wrap + renderiza torto. Trocamos por ASCII e removemos o
// que sobrar fora de Latin-1. Mantém acentos pt/es e o bullet "•".
function pdfSafe(s) {
  return String(s)
    .replace(/—/g, ' - ')   // — em dash
    .replace(/–/g, '-')     // – en dash
    .replace(/[‘’‛′]/g, "'")
    .replace(/[“”‟″]/g, '"')
    .replace(/…/g, '...')   // …
    .replace(/→/g, '->')    // →
    .replace(/▸/g, '>')     // ▸
    .replace(/ /g, ' ')     // nbsp
    .replace(/[^\x09\x0A\x0D\x20-\x7E¡-ÿ•]/g, ''); // resto fora de Latin-1 (mantém •)
}

// Wrap manual e confiável (getTextWidth com a fonte/tamanho ATUAIS): garante que
// NENHUMA linha estoura maxWidth (com hard-break de palavra gigante). Substitui o
// splitTextToSize, que subestimava a largura e deixava o título vazar a margem.
function wrapText(doc, text, maxWidth) {
  const lines = [];
  let cur = '';
  String(text).split(/\s+/).forEach((word) => {
    let w = word;
    while (doc.getTextWidth(w) > maxWidth && w.length > 1) {
      let i = w.length;
      while (i > 1 && doc.getTextWidth(w.slice(0, i)) > maxWidth) i -= 1;
      if (cur) { lines.push(cur); cur = ''; }
      lines.push(w.slice(0, i));
      w = w.slice(i);
    }
    const trial = cur ? `${cur} ${w}` : w;
    if (cur && doc.getTextWidth(trial) > maxWidth) { lines.push(cur); cur = w; }
    else cur = trial;
  });
  if (cur) lines.push(cur);
  return lines.length ? lines : [''];
}

// Duração em segundos -> "m:ss" ou "h:mm:ss".
function fmtDur(s) {
  s = Math.round(Number(s) || 0);
  const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); const sec = s % 60;
  const p = (n) => String(n).padStart(2, '0');
  return h ? `${h}:${p(m)}:${p(sec)}` : `${m}:${p(sec)}`;
}

// ── Construção do modelo (nós) ───────────────────────────────────────────────
// Cada nó: { type: 'h1'|'h2'|'text'|'bullet'|'spacer', text }
export function buildExportModel(analysis, reports, t) {
  const tt = (k, fb) => {
    const v = t(`dobraNicho.${k}`);
    return v === `dobraNicho.${k}` ? (fb ?? k) : v; // fallback se a chave faltar
  };
  const r = (analysis && analysis.result) || {};
  const reps = reports || {};
  const repOf = (i) => reps[i] ?? reps[String(i)] ?? null;
  const nodes = [];
  const H1 = (text) => text && nodes.push({ type: 'h1', text: String(text) });
  const H2 = (text) => text && nodes.push({ type: 'h2', text: String(text) });
  const P = (text) => { const s = (text ?? '').toString().trim(); if (s) nodes.push({ type: 'text', text: s }); };
  const KV = (k, v) => { if (v !== undefined && v !== null && `${v}` !== '') nodes.push({ type: 'text', text: `${k}: ${v}` }); };
  const LIST = (label, items) => {
    const arr = (items || []).filter((x) => x !== undefined && x !== null && `${x}`.trim() !== '');
    if (!arr.length) return;
    if (label) nodes.push({ type: 'text', text: `${label}:` });
    arr.forEach((it) => nodes.push({ type: 'bullet', text: String(it) }));
  };
  const SP = () => nodes.push({ type: 'spacer' });
  const num = (n) => (typeof n === 'number' ? n.toLocaleString('pt-BR') : (n || ''));

  const dossier = r.dossier || {};
  const anc = r.ancoragem || {};
  const pub = anc.publico || {};
  const pv = r.padrao_vencedor || {};
  const ff = r.forma_funcao || {};
  const funcao = ff.funcao || {};
  const forma = ff.forma || {};
  const ins = r.insights || null;
  const ops = r.oportunidades || [];

  const channelName = (ins && ins.channel_info && ins.channel_info.title) || dossier.nome || 'Canal';

  // ── Cabeçalho ──────────────────────────────────────────────────────────────
  H1(`${tt('dna_title', 'Análise do canal')}: ${channelName}`);
  const today = new Date().toISOString().slice(0, 10);
  KV(tt('export_generated', 'Gerado em'), today);
  if (dossier.idioma) KV(tt('dna_lang', 'idioma'), dossier.idioma);
  if (r.confianca) P(tt(`confidence_${r.confianca}`, r.confianca));
  SP();

  // ── 1. DNA / Padrão vencedor ────────────────────────────────────────────────
  H1(tt('export_section_dna', 'Análise do canal (DNA)'));
  H2(tt('dna_title', 'Ancoragem'));
  KV(tt('dna_main_niche', 'Nicho principal'), anc.nicho_principal);
  KV(tt('dna_subniche', 'Subnicho atual'), anc.subnicho_atual);
  KV(tt('dna_audience', 'Público'), [pub.demografico, pub.psicografico].filter(Boolean).join(' · '));
  KV(tt('dna_territory', 'Território'), anc.territorio);
  SP();
  H2(tt('export_channel_metrics', 'Métricas do canal'));
  KV(tt('dna_subs', 'inscritos'), num(dossier.subscriber_count));
  KV(tt('dna_views', 'views totais'), num(dossier.channel_views));
  KV(tt('dna_videos', 'analisados'), `${num(dossier.sample_size)} / ${num(dossier.video_count)}`);
  KV(tt('dna_outliers', 'outliers'), num(dossier.outliers_count));
  KV(tt('dna_top_outlier', 'maior outlier'), dossier.top_outlier_ratio ? `${dossier.top_outlier_ratio}x` : '');
  SP();
  H2(tt('dna_winning_pattern', 'Padrão vencedor'));
  P(pv.resumo);
  LIST(tt('dna_common', 'O que os vencedores têm em comum'), pv.o_que_os_vencedores_tem_em_comum);
  LIST(tt('dna_triggers', 'Gatilhos vencedores'), pv.gatilhos_vencedores);
  LIST(tt('dna_formulas', 'Fórmulas de título'), pv.formulas_de_titulo_vencedoras);
  if ((pv.evidencias_de_performance || []).length) {
    nodes.push({ type: 'text', text: `${tt('dna_evidence', 'Evidências de performance')}:` });
    pv.evidencias_de_performance.forEach((e) => {
      const bits = [e.titulo, e.views != null ? `${num(e.views)} views` : '', e.overperformance_ratio ? `${e.overperformance_ratio}x` : ''].filter(Boolean);
      nodes.push({ type: 'bullet', text: bits.join(' · ') });
    });
  }
  SP();
  H2(tt('dna_function', 'Função vencedora'));
  KV('Promessa', funcao.promessa);
  KV('Emoção dominante', funcao.emocao_dominante);
  KV('Fantasia de identidade', funcao.fantasia_identidade);
  LIST('Temas', forma.temas);
  SP();

  // ── 2. Insights de canal (opcional) ──────────────────────────────────────────
  if (ins) {
    const s = ins.summary || {};
    H1(tt('export_section_insights', 'Insights de canal'));
    H2(tt('ins_kpis', 'Métricas'));
    KV(tt('ins_avg_views', 'Média de views'), num(s.avg_views));
    KV(tt('ins_median_views', 'Mediana de views'), num(s.median_views));
    KV(tt('ins_engagement', 'Engajamento médio'), s.avg_engagement != null ? `${s.avg_engagement}%` : '');
    KV(tt('ins_duration', 'Duração média'), s.avg_duration ? `${Math.round(s.avg_duration / 60)} min` : '');
    KV(tt('ins_vpd', 'Views por dia'), num(Math.round(s.avg_views_per_day || 0)));
    KV(tt('dna_videos', 'analisados'), num(s.total_videos_analyzed));
    SP();
    const vids = ins.videos || ins.top_videos || []; // novo: lista completa; fallback p/ análises antigas
    if (vids.length) {
      // Top do canal (destaque rápido — os 10 de maior view).
      H2(tt('ins_top_videos', 'Top vídeos'));
      vids.slice(0, 10).forEach((v, i) => {
        const bits = [v.view_count != null ? `${num(v.view_count)} views` : '', v.engagement_rate != null ? `${v.engagement_rate}% eng` : ''].filter(Boolean);
        nodes.push({ type: 'bullet', text: `#${i + 1}  ${v.title || ''}${bits.length ? '  ·  ' + bits.join(' · ') : ''}` });
      });
      SP();
      // TODOS os vídeos analisados, com dados por vídeo + LINK.
      H2(`${tt('ins_all_videos', 'Vídeos analisados')} (${vids.length})`);
      vids.forEach((v, i) => {
        nodes.push({ type: 'bullet', text: `#${i + 1}  ${v.title || ''}` });
        const metrics = [
          v.view_count != null ? `${num(v.view_count)} views` : '',
          v.like_count != null ? `${num(v.like_count)} likes` : '',
          v.comment_count != null ? `${num(v.comment_count)} coment.` : '',
          v.engagement_rate != null ? `${v.engagement_rate}% eng` : '',
          v.duration_seconds ? fmtDur(v.duration_seconds) : '',
          (v.published_at || '').slice(0, 10),
        ].filter(Boolean).join(' · ');
        if (metrics) nodes.push({ type: 'text', text: metrics, indent: 14 });
        if (v.url) nodes.push({ type: 'text', text: v.url, indent: 14 });
      });
      SP();
    }
    if ((ins.buckets || []).length) {
      H2(tt('ins_themes', 'Temas de conteúdo'));
      ins.buckets.forEach((b) => nodes.push({ type: 'bullet', text: `${b.name} · ${num(b.total_videos)} vídeos · ${num(b.median_views)} ${tt('ins_median_views', 'mediana')}` }));
      SP();
    }
    if ((ins.title_patterns || []).length) {
      H2(tt('ins_title_formats', 'Padrões de título'));
      ins.title_patterns.forEach((p) => nodes.push({ type: 'bullet', text: `${p.name} · ${num(p.total_videos)} vídeos · ${num(p.median_views)} ${tt('ins_median_views', 'mediana')}` }));
      SP();
    }
    if ((ins.insights || []).length) {
      H2(tt('ins_insights', 'Insights'));
      ins.insights.forEach((i) => nodes.push({ type: 'bullet', text: i.title ? `${i.title}: ${i.message || ''}` : (i.message || '') }));
      SP();
    }
  }

  // ── 3. Oportunidades ─────────────────────────────────────────────────────────
  H1(tt('export_section_opportunities', 'Oportunidades'));
  ops.forEach((op, i) => {
    const typeLabel = op.tipo === 'mar_de_ideias'
      ? `${tt('type_mar_de_ideias', 'Mar de Ideias')} · ${tt(`subtipo_${op.subtipo || 'nova_visao'}`, op.subtipo || '')}`
      : tt(`type_${op.tipo}`, op.tipo);
    H2(`${typeLabel} — ${op.label || ''}`);
    P(op.rationale);
    KV(tt('dna_function', 'Função preservada'), op.funcao_preservada);
    if (op.search_query) KV('Search query', op.search_query);
    if (op.sinal_de_oportunidade) P(op.sinal_de_oportunidade);

    const rep = repOf(i);
    if (rep) {
      P(rep.conceito);
      LIST(tt('preserves', 'O que preserva'), rep.o_que_preserva_do_original);
      LIST(tt('changes', 'O que muda'), rep.o_que_muda);

      const niches = rep.nichos_sugeridos || [];
      if (niches.length) {
        niches.forEach((nch) => {
          nodes.push({ type: 'text', text: `▸ ${nch.nome || nch.subnicho || ''}` });
          KV(tt('target_niche', 'Nicho novo'), nch.nicho);
          KV(tt('target_subniche', 'Subnicho'), nch.subnicho);
          KV(tt('target_audience', 'Público-alvo'), nch.publico_alvo);
          P(nch.por_que_encaixa);
          P(nch.ideia_tema);
          if (nch.como_aplicar) KV(tt('how_to_apply', 'Como aplicar'), nch.como_aplicar);
          LIST(tt('ready_titles', 'Títulos prontos'), nch.titulos);
          (nch.mapa_de_equivalencia || []).forEach((m) => {
            if (m && (m.titulo_novo || m.titulo_original)) nodes.push({ type: 'bullet', text: `${m.titulo_original || ''} → ${m.titulo_novo || ''}` });
          });
          SP();
        });
      } else {
        // oportunidade individual (mar_de_ideias / adaptacao_mercado)
        if (rep.recorte_alvo) KV(tt('target_idea', 'Recorte alvo'), rep.recorte_alvo);
        if (rep.como_aplicar) KV(tt('how_to_apply', 'Como aplicar'), rep.como_aplicar);
        LIST(tt('ready_titles', 'Títulos prontos'), rep.titulos_prontos);
        (rep.mapa_de_equivalencia || []).forEach((m) => {
          if (m && (m.titulo_novo || m.titulo_original)) nodes.push({ type: 'bullet', text: `${m.titulo_original || ''} → ${m.titulo_novo || ''}` });
        });
        LIST(tt('break_alerts', 'Alertas de quebra'), rep.alertas_de_quebra);
      }
    }
    SP();
  });

  return { nodes, channelName };
}

// ── slug + nome de arquivo ───────────────────────────────────────────────────
function fileBase(channelName) {
  const slug = (channelName || 'canal').replace(/[^a-z0-9]/gi, '_').replace(/_+/g, '_').replace(/^_|_$/g, '').toLowerCase() || 'canal';
  return `dobra_${slug}_${new Date().toISOString().slice(0, 10)}`;
}

// ── TXT ──────────────────────────────────────────────────────────────────────
function nodesToText(nodes) {
  const out = [];
  nodes.forEach((n) => {
    if (n.type === 'h1') { out.push('', '='.repeat(60), n.text.toUpperCase(), '='.repeat(60)); }
    else if (n.type === 'h2') { out.push('', `── ${n.text} ──`); }
    else if (n.type === 'bullet') { out.push(`  • ${n.text}`); }
    else if (n.type === 'spacer') { out.push(''); }
    else { out.push(`${' '.repeat(n.indent || 0)}${n.text}`); }
  });
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim() + '\n';
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function downloadTxt(analysis, reports, t) {
  const { nodes, channelName } = buildExportModel(analysis, reports, t);
  const blob = new Blob([nodesToText(nodes)], { type: 'text/plain;charset=utf-8' });
  triggerDownload(blob, `${fileBase(channelName)}.txt`);
}

// ── PDF (jspdf) — com marca DarkPlanner ──────────────────────────────────────
// Layout: faixa de cabeçalho (logo + nome + site) e rodapé (site + paginação) em
// TODAS as páginas, marca d'água (logo + "DarkPlanner") atrás do conteúdo, cores
// da marca. Acentos pt/es = Latin-1 (Helvetica padrão). É async (carrega a logo).
export async function downloadPdf(analysis, reports, t) {
  try {
    const { nodes, channelName } = buildExportModel(analysis, reports, t);
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();   // 595
    const pageH = doc.internal.pageSize.getHeight();  // 842

    // ── Marca / paleta ──────────────────────────────────────────────────────
    const NAVY = [10, 14, 23];     // #0A0E17 (faixa)
    const BLUE = [13, 174, 255];   // #0DAEFF (acento/títulos)
    const DARK = [38, 38, 38];     // corpo
    const GRAY = [110, 110, 110];  // secundário
    const WM = [236, 239, 243];    // marca d'água (cinza bem claro)

    const M = 44;                  // margem lateral
    const HEADER_H = 50;           // faixa do topo
    const TOP = HEADER_H + 22;     // onde o conteúdo começa
    const BOTTOM = pageH - 40;     // limite antes do rodapé
    const maxW = pageW - M * 2;

    const logo = await loadDataUrl(LOGO_URL);
    const hasGState = typeof doc.GState === 'function';
    const setColor = (c) => doc.setTextColor(c[0], c[1], c[2]);

    // Cabeçalho + marca d'água — desenhados ANTES do conteúdo de cada página
    // (marca d'água fica ATRÁS do texto).
    const drawChrome = () => {
      // marca d'água: logo grande + "DarkPlanner", bem apagados, centralizados
      if (logo && hasGState) {
        doc.setGState(new doc.GState({ opacity: 0.05 }));
        const wm = 300;
        try { doc.addImage(logo, 'PNG', (pageW - wm) / 2, (pageH - wm) / 2 - 40, wm, wm); } catch { /* ignora */ }
        doc.setGState(new doc.GState({ opacity: 1 }));
      }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(46); setColor(WM);
      doc.text('DarkPlanner', pageW / 2, pageH / 2 + 150, { align: 'center' });
      // faixa do topo (navy) + acento azul
      doc.setFillColor(NAVY[0], NAVY[1], NAVY[2]);
      doc.rect(0, 0, pageW, HEADER_H, 'F');
      doc.setFillColor(BLUE[0], BLUE[1], BLUE[2]);
      doc.rect(0, HEADER_H, pageW, 2.5, 'F');
      if (logo) { try { doc.addImage(logo, 'PNG', M, 12, 26, 26); } catch { /* ignora */ } }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.setTextColor(255, 255, 255);
      doc.text('DarkPlanner', M + (logo ? 34 : 0), 30);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5); doc.setTextColor(150, 200, 230);
      doc.text(SITE_URL, pageW - M, 30, { align: 'right' });
    };

    const drawFooter = (page, total) => {
      doc.setDrawColor(225, 225, 225); doc.setLineWidth(0.5);
      doc.line(M, pageH - 30, pageW - M, pageH - 30);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); setColor(GRAY);
      doc.text(SITE_URL, M, pageH - 18);
      doc.text(`${page} / ${total}`, pageW - M, pageH - 18, { align: 'right' });
    };

    let y = TOP;
    drawChrome();
    const ensure = (h) => { if (y + h > BOTTOM) { doc.addPage(); drawChrome(); y = TOP; } };
    const write = (text, { size = 11, bold = false, color = DARK, indent = 0, gap = 4, hang = 0 } = {}) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(size);
      setColor(color);
      const lines = wrapText(doc, pdfSafe(text), maxW - indent - hang);
      const lh = size * 1.32;
      lines.forEach((ln, idx) => { ensure(lh); doc.text(ln, M + indent + (idx ? hang : 0), y); y += lh; });
      y += gap;
    };

    nodes.forEach((n) => {
      if (n.type === 'h1') { y += 8; ensure(24); write(n.text, { size: 15, bold: true, color: BLUE, gap: 7 }); }
      else if (n.type === 'h2') { ensure(20); write(n.text, { size: 12.5, bold: true, color: BLUE, gap: 4 }); }
      else if (n.type === 'bullet') { write(`•  ${n.text}`, { size: 10.5, color: DARK, indent: 10, gap: 2, hang: 12 }); }
      else if (n.type === 'spacer') { y += 6; }
      else { write(n.text, { size: 10.5, color: GRAY, indent: n.indent || 0, gap: 3 }); }
    });

    // Rodapé + paginação ("i / N") em todas as páginas, no fim (precisa do total).
    const total = doc.getNumberOfPages();
    for (let p = 1; p <= total; p += 1) { doc.setPage(p); drawFooter(p, total); }

    doc.save(`${fileBase(channelName)}.pdf`);
  } catch (err) {
    toast.error(t ? t('dobraNicho.export_error') : 'Não foi possível gerar o PDF.');
  }
}
